import { createSlice } from '@reduxjs/toolkit';
import { AsyncStatus } from '../../../libs/utils/constants/async-status';
import ISearch from '../../../libs/utils/interfaces/search-interface';
import {
    editFavoriteFilters,
    getAllActiveSkills,
    getAllHSearchSkills,
    getBrowseSkillsOrPatientEducations,
    getCombinedAutoSuggest,
    getFavoriteFilters,
    getKeywordAutoSuggest,
    getPEAutoSuggest,
    getPEContentFromHSCS,
    getQuickLinks,
    getRecentViewedSkills,
    postAddFavoriteFilters,
} from './search-thunk';

interface ISearchPageState {
    keyword: string;
    contentType: string;
    ageSpecific: string;
    subType: string;
    category: string;
    speciality: string;
    sortItem: string;
    sortOrder: string;
    currentPage: number;
    isSubTypeMedication: boolean;
    selectedRadioOption: { [key: string]: string };
    isOverlayVisible: boolean;
}

const initialSearchPageState: ISearchPageState = {
    keyword: '',
    contentType: '',
    ageSpecific: '',
    subType: '',
    category: '',
    speciality: '',
    sortItem: '',
    sortOrder: 'asc',
    currentPage: 1,
    isSubTypeMedication: false,
    selectedRadioOption: {},
    isOverlayVisible: false,
};
const initialState: ISearch = {
    asyncStatus: AsyncStatus.INITIAL,
    activeSkillsDetails: [],
    statusCode: 0,
    asyncHSearchStatus: AsyncStatus.INITIAL,
    hSearchSkillsDetails: [],
    hSearchFacetData: [],
    hSearchStatusCode: 0,
    hSearchTotalRows: 0,
    asyncAddOrEditFavoriteFiltersStatus: AsyncStatus.INITIAL,
    addOrEditFavoriteFilterStatusCode: 0,
    asyncGetFavoriteFiltersStatus: AsyncStatus.INITIAL,
    getFavoriteFilterStatusCode: 0,
    favoriteFilters: '',
    hscsAsyncStatus: AsyncStatus.INITIAL,
    hscsContentDetails: '',
    hscsContentStatusCode: 0,
    quickLinksAsyncStatus: AsyncStatus.INITIAL,
    quickLinks: [],
    quickLinksStatusCode: 0,
    peAutoSuggestStatus: AsyncStatus.INITIAL,
    peAutoSuggestData: [],
    recentViewedSkillsAsyncStatus: AsyncStatus.INITIAL,
    recentViewedSkills: [],
    recentViewedSkillsStatusCode: 0,
    keywordAutoSuggestStatus: AsyncStatus.INITIAL,
    keywordAutoSuggestData: [],
    searchPageState: initialSearchPageState,
    isNavigatingBackToSearch: false,
};
const searchSlice = createSlice({
    name: 'search',
    initialState,
    reducers: {
        resetAutoSuggestData: (state: ISearch) => {
            state.peAutoSuggestStatus = AsyncStatus.INITIAL;
            state.peAutoSuggestData = [];
            state.keywordAutoSuggestStatus = AsyncStatus.INITIAL;
            state.keywordAutoSuggestData = [];
        },
        saveSearchPageState: (state: ISearch, action) => {
            state.searchPageState = {
                ...state.searchPageState,
                ...action.payload,
            };
        },
        setNavigatingBackToSearch: (state: ISearch, action) => {
            state.isNavigatingBackToSearch = action.payload;
        },
        resetSearchPageState: (state: ISearch) => {
            state.searchPageState = initialSearchPageState;
            state.isNavigatingBackToSearch = false;
        },
        resetFacetData: (state: ISearch) => {
            state.hSearchFacetData = [];
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(getAllActiveSkills.pending, (state: ISearch) => {
                state.asyncStatus = AsyncStatus.LOADING;
                state.asyncAddOrEditFavoriteFiltersStatus = AsyncStatus.INITIAL;
                state.statusCode = 0;
            })
            .addCase(
                getAllActiveSkills.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.asyncStatus = AsyncStatus.SUCCESS;
                    state.activeSkillsDetails = payload?.response?.rows;
                    state.statusCode = payload?.response?.status;
                }
            )
            .addCase(getAllActiveSkills.rejected, (state: ISearch) => {
                state.asyncStatus = AsyncStatus.FAILURE;
                state.statusCode = 0;
            })
            .addCase(getAllHSearchSkills.pending, (state: ISearch) => {
                state.asyncHSearchStatus = AsyncStatus.LOADING;
                state.asyncAddOrEditFavoriteFiltersStatus = AsyncStatus.INITIAL;
                state.hSearchSkillsDetails = [];
                state.hSearchTotalRows = 0;
                state.statusCode = 0;
            })
            .addCase(
                getAllHSearchSkills.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.hSearchSkillsDetails =
                        payload?.data?.searchResult?.docs;
                    state.hSearchFacetData = payload?.data?.facetCounts;
                    state.hSearchTotalRows =
                        payload?.data?.searchResult?.numFound;
                    state.hSearchStatusCode = payload?.status;
                    state.asyncHSearchStatus = AsyncStatus.SUCCESS;
                }
            )
            .addCase(getAllHSearchSkills.rejected, (state: ISearch) => {
                state.asyncHSearchStatus = AsyncStatus.FAILURE;
                state.hSearchStatusCode = 0;
            })
            .addCase(postAddFavoriteFilters.pending, (state: ISearch) => {
                state.asyncAddOrEditFavoriteFiltersStatus = AsyncStatus.LOADING;
                state.addOrEditFavoriteFilterStatusCode = 0;
            })
            .addCase(
                postAddFavoriteFilters.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.asyncAddOrEditFavoriteFiltersStatus =
                        AsyncStatus.SUCCESS;
                    state.addOrEditFavoriteFilterStatusCode = payload?.status;
                    state.favoriteFilters =
                        payload?.status === 200
                            ? JSON.parse(payload?.data?.data?.filterContent)
                            : '';
                }
            )
            .addCase(postAddFavoriteFilters.rejected, (state: ISearch) => {
                state.asyncAddOrEditFavoriteFiltersStatus = AsyncStatus.FAILURE;
                state.addOrEditFavoriteFilterStatusCode = 0;
            })
            // get favorite filter
            .addCase(getFavoriteFilters.pending, (state: ISearch) => {
                state.asyncGetFavoriteFiltersStatus = AsyncStatus.LOADING;
                state.getFavoriteFilterStatusCode = 0;
            })
            .addCase(
                getFavoriteFilters.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.asyncGetFavoriteFiltersStatus = AsyncStatus.SUCCESS;
                    state.getFavoriteFilterStatusCode = payload?.status;
                    state.favoriteFilters =
                        payload?.status === 200
                            ? JSON.parse(
                                  payload?.data?.userFavoriteFilter
                                      ?.filterContent
                              )
                            : '';
                }
            )
            .addCase(getFavoriteFilters.rejected, (state: ISearch) => {
                state.asyncGetFavoriteFiltersStatus = AsyncStatus.FAILURE;
                state.getFavoriteFilterStatusCode = 0;
            })
            // edit favorite filter
            .addCase(editFavoriteFilters.pending, (state: ISearch) => {
                state.asyncAddOrEditFavoriteFiltersStatus = AsyncStatus.LOADING;
                state.addOrEditFavoriteFilterStatusCode = 0;
            })
            .addCase(
                editFavoriteFilters.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.asyncAddOrEditFavoriteFiltersStatus =
                        AsyncStatus.SUCCESS;
                    state.addOrEditFavoriteFilterStatusCode = payload?.status;
                    state.favoriteFilters = payload?.filterContent;
                }
            )
            .addCase(editFavoriteFilters.rejected, (state: ISearch) => {
                state.asyncAddOrEditFavoriteFiltersStatus = AsyncStatus.FAILURE;
                state.addOrEditFavoriteFilterStatusCode = 0;
            })
            // get all HSCS content
            .addCase(getPEContentFromHSCS.pending, (state: ISearch) => {
                state.hscsAsyncStatus = AsyncStatus.LOADING;
                state.hscsContentDetails = '';
                state.statusCode = 0;
            })
            .addCase(
                getPEContentFromHSCS.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.hscsAsyncStatus = AsyncStatus.SUCCESS;
                    state.hscsContentDetails = payload?.data?.response;
                    state.hscsContentStatusCode = payload?.status;
                }
            )
            .addCase(getPEContentFromHSCS.rejected, (state: ISearch) => {
                state.hscsAsyncStatus = AsyncStatus.FAILURE;
                state.hscsContentStatusCode = 0;
            })

            .addCase(getQuickLinks.pending, (state: ISearch) => {
                state.quickLinksAsyncStatus = AsyncStatus.LOADING;
                state.quickLinksStatusCode = 0;
            })
            .addCase(
                getQuickLinks.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.quickLinksAsyncStatus = AsyncStatus.SUCCESS;
                    state.quickLinks = payload?.data.courseContentInfo;
                    state.quickLinksStatusCode = payload?.status;
                }
            )
            .addCase(getQuickLinks.rejected, (state: ISearch) => {
                state.quickLinksAsyncStatus = AsyncStatus.FAILURE;
                state.quickLinksStatusCode = 0;
            })
            .addCase(getPEAutoSuggest.pending, (state: ISearch) => {
                state.peAutoSuggestStatus = AsyncStatus.LOADING;
                state.peAutoSuggestData = [];
            })
            .addCase(
                getPEAutoSuggest.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.peAutoSuggestStatus = AsyncStatus.SUCCESS;
                    state.peAutoSuggestData = payload;
                }
            )
            .addCase(getPEAutoSuggest.rejected, (state: ISearch) => {
                state.peAutoSuggestStatus = AsyncStatus.FAILURE;
                state.peAutoSuggestData = [];
            })
            .addCase(getRecentViewedSkills.pending, (state: ISearch) => {
                state.recentViewedSkillsAsyncStatus = AsyncStatus.LOADING;
                state.recentViewedSkillsStatusCode = 0;
            })
            .addCase(
                getRecentViewedSkills.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.recentViewedSkillsAsyncStatus = AsyncStatus.SUCCESS;
                    state.recentViewedSkills = payload;
                }
            )
            .addCase(getRecentViewedSkills.rejected, (state: ISearch) => {
                state.recentViewedSkillsAsyncStatus = AsyncStatus.FAILURE;
                state.recentViewedSkillsStatusCode = 0;
            })
            .addCase(getKeywordAutoSuggest.pending, (state: ISearch) => {
                state.keywordAutoSuggestStatus = AsyncStatus.LOADING;
                state.keywordAutoSuggestData = [];
            })
            .addCase(
                getKeywordAutoSuggest.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.keywordAutoSuggestStatus = AsyncStatus.SUCCESS;
                    state.keywordAutoSuggestData = payload;
                }
            )
            .addCase(getKeywordAutoSuggest.rejected, (state: ISearch) => {
                state.keywordAutoSuggestStatus = AsyncStatus.FAILURE;
                state.keywordAutoSuggestData = [];
            })
            .addCase(
                getBrowseSkillsOrPatientEducations.pending,
                (state: ISearch) => {
                    state.asyncHSearchStatus = AsyncStatus.LOADING;
                    state.asyncAddOrEditFavoriteFiltersStatus =
                        AsyncStatus.INITIAL;
                    state.hSearchSkillsDetails = [];
                    state.hSearchTotalRows = 0;
                    state.statusCode = 0;
                }
            )
            .addCase(
                getBrowseSkillsOrPatientEducations.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.asyncHSearchStatus = AsyncStatus.SUCCESS;
                    state.hSearchSkillsDetails =
                        payload?.data?.searchResult?.docs;
                    state.hSearchFacetData = payload?.data?.facetCounts;
                    state.hSearchTotalRows =
                        payload?.data?.searchResult?.numFound;
                    state.hSearchStatusCode = payload?.status;
                }
            )
            .addCase(
                getBrowseSkillsOrPatientEducations.rejected,
                (state: ISearch) => {
                    state.asyncHSearchStatus = AsyncStatus.FAILURE;
                    state.hSearchStatusCode = 0;
                }
            )
            .addCase(getCombinedAutoSuggest.pending, (state: ISearch) => {
                state.peAutoSuggestStatus = AsyncStatus.LOADING;
                state.keywordAutoSuggestStatus = AsyncStatus.LOADING;
                state.peAutoSuggestData = [];
                state.keywordAutoSuggestData = [];
            })
            .addCase(
                getCombinedAutoSuggest.fulfilled,
                (state: ISearch, { payload }: any) => {
                    state.peAutoSuggestData = payload.peAutoSuggest;
                    state.keywordAutoSuggestData = payload.keywordAutoSuggest;
                    state.peAutoSuggestStatus = AsyncStatus.SUCCESS;
                    state.keywordAutoSuggestStatus = AsyncStatus.SUCCESS;
                }
            )
            .addCase(getCombinedAutoSuggest.rejected, (state: ISearch) => {
                state.peAutoSuggestData = [];
                state.keywordAutoSuggestData = [];
                state.peAutoSuggestStatus = AsyncStatus.FAILURE;
                state.keywordAutoSuggestStatus = AsyncStatus.FAILURE;
            });
    },
});
export const {
    resetAutoSuggestData,
    saveSearchPageState,
    setNavigatingBackToSearch,
    resetSearchPageState,
    resetFacetData,
} = searchSlice.actions;
export default searchSlice.reducer;
